import { API_BASE_URL } from "../config/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import EngineerLayout from "../components/EngineerLayout";
import PhoneActions from "../components/PhoneActions";
import { downloadReportPdf } from "../utils/reportApi";

const normalizeStatus = (value = "") => {
  const status = String(value || "").toLowerCase().trim();
  if (status === "created") return "Assigned";
  if (status === "closed") return "Completed";
  if (status === "revisit" || status === "re visit" || status === "re-visit") return "Re-Visit";
  if (status === "completed") return "Completed";
  if (status === "assigned") return "Assigned";
  if (status === "in progress") return "In Progress";
  if (status === "customer waiting for order" || status === "need to provide quote") return "Need to Provide Quote";
  if (status === "other") return "Other";
  return "Pending";
};

const normalizeApprovalStatus = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "approved") return "Approved";
  if (normalized === "edit access granted") return "Edit Access Granted";
  if (normalized === "re-approved/locked" || normalized === "re-approved") return "Re-Approved/Locked";
  if (normalized === "changes requested" || normalized === "rejected" || normalized === "not approved") {
    return "Changes Requested";
  }
  return "Approval Pending";
};

const isNewerReport = (candidate, current) =>
  new Date(candidate?.updatedAt || candidate?.createdAt || 0) >
  new Date(current?.updatedAt || current?.createdAt || 0);

const canCreateReportFromTravel = (travel) => {
  const status = String(travel?.status || "").trim().toLowerCase();
  return status === "at site" || status === "left site" || status === "completed";
};

function MyComplaints() {
  const [complaints, setComplaints] = useState([]);
  const [reportByComplaintId, setReportByComplaintId] = useState({});
  const [travelByComplaintId, setTravelByComplaintId] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [acceptingComplaintId, setAcceptingComplaintId] = useState("");
  const [updatingTravelId, setUpdatingTravelId] = useState("");
  const navigate = useNavigate();

  const engineerName = localStorage.getItem("engineerName");
  const engineerEmail = localStorage.getItem("engineerEmail");
  const engineerId = localStorage.getItem("engineerId");
  const engineerLookup =
    engineerId ||
    engineerEmail ||
    engineerName;

  const engineerMatches = useMemo(
    () =>
      [engineerName, engineerEmail, engineerId, engineerLookup]
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean),
    [engineerEmail, engineerId, engineerLookup, engineerName]
  );

  const fetchComplaints = useCallback(async () => {
    try {
      if (!engineerLookup) {
        setComplaints([]);
        return;
      }

      setLoading(true);

      const [complaintsRes, reportsRes, travelHistoryRes] = await Promise.all([
        axios.get(API_BASE_URL + `/api/complaints/engineer/${encodeURIComponent(engineerLookup)}`),
        axios.get(API_BASE_URL + "/api/visits", {
          params: { engineer: engineerLookup, excludePdf: 1 }
        }),
        axios.get(API_BASE_URL + "/api/travel")
      ]);

      const complaintData = Array.isArray(complaintsRes.data) ? complaintsRes.data : [];
      const reports = Array.isArray(reportsRes.data) ? reportsRes.data : [];
      const travels = (Array.isArray(travelHistoryRes.data) ? travelHistoryRes.data : []).filter((travel) =>
        engineerMatches.includes(String(travel.engineerId || "").trim().toLowerCase()) ||
        engineerMatches.includes(String(travel.engineerName || "").trim().toLowerCase())
      );
      const reportMap = reports.reduce((acc, report) => {
        [report.complaintId, report.complaintRefId]
          .filter(Boolean)
          .forEach((key) => {
            const normalizedKey = String(key);
            if (!acc[normalizedKey] || isNewerReport(report, acc[normalizedKey])) {
              acc[normalizedKey] = report;
            }
          });
        return acc;
      }, {});
      const travelMap = travels.reduce((acc, travel) => {
        [travel.complaintId, travel.complaintRefId]
          .filter(Boolean)
          .forEach((key) => {
            const normalizedKey = String(key);
            const current = acc[normalizedKey];
            if (
              !current ||
              new Date(travel.updatedAt || travel.createdAt || 0) >
                new Date(current.updatedAt || current.createdAt || 0)
            ) {
              acc[normalizedKey] = travel;
            }
          });
        return acc;
      }, {});

      setComplaints(complaintData);
      setReportByComplaintId(reportMap);
      setTravelByComplaintId(travelMap);
    } catch (error) {
      console.error("Error fetching engineer complaints:", error);
      setComplaints([]);
      setReportByComplaintId({});
      setTravelByComplaintId({});
    } finally {
      setLoading(false);
    }
  }, [engineerLookup, engineerMatches]);

  useEffect(() => {
    fetchComplaints();
  }, [fetchComplaints]);

  const acceptComplaint = async (complaintId) => {
    try {
      setAcceptingComplaintId(complaintId);
      await axios.put(API_BASE_URL + `/api/complaints/${complaintId}/accept`, {
        engineer: engineerLookup
      });
      await fetchComplaints();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to accept complaint");
    } finally {
      setAcceptingComplaintId("");
    }
  };

  const formatAddress = (address) => {
    if (!address) return "-";
    if (typeof address === "string") return address;

    return [
      address.street,
      address.area,
      address.city,
      address.state,
      address.pincode
    ]
      .filter(Boolean)
      .join(", ");
  };

  const getNextTravelAction = (travel) => {
    const status = travel?.status || "Not Started";

    if (status === "Not Started") {
      return { label: "Start Journey", status: "Started", timeField: "startTime" };
    }
    if (status === "Started") {
      return { label: "At Site", status: "At Site", timeField: "reachSiteTime" };
    }
    if (status === "At Site") {
      return { label: "Left Site", status: "Left Site", timeField: "leaveSiteTime" };
    }
    if (status === "Left Site") {
      return { label: "Reached Home", status: "Completed", timeField: "reachHomeTime" };
    }

    return null;
  };

  const handleTravelAction = async (complaint) => {
    const existingTravel =
      travelByComplaintId[complaint._id] ||
      travelByComplaintId[complaint.complaintId];
    const nextAction = getNextTravelAction(existingTravel);

    if (!nextAction) {
      return;
    }

    try {
      setUpdatingTravelId(complaint._id);

      let activeTravel = existingTravel;

      if (!activeTravel) {
        let location = "";
        let city = "";
        const validCustomerId =
          complaint.customerId &&
          complaint.customerId !== "ABB-WARRANTY" &&
          /^[a-f\d]{24}$/i.test(String(complaint.customerId));

        if (validCustomerId) {
          try {
            const customerRes = await axios.get(API_BASE_URL + `/api/customers/${complaint.customerId}`);
            const customer = customerRes.data;
            location = formatAddress(customer?.address);
            city = customer?.address?.city || "";
          } catch (e) {
            console.warn("Could not fetch customer details for travel creation", e);
          }
        }

        if (!location || location === "-") {
          location = formatAddress(complaint.address);
          if (!location || location === "-") {
            location = complaint.location || "";
          }
        }

        if (!city) {
          if (complaint.address && typeof complaint.address === "object") {
            city = complaint.address.city || "";
          } else if (complaint.city) {
            city = complaint.city || "";
          }
        }

        const createResponse = await axios.post(API_BASE_URL + "/api/travel", {
          engineerId: engineerLookup,
          engineerName,
          complaintId: complaint._id,
          complaintRefId: complaint.complaintId,
          customerId: complaint.customerId || "",
          company: complaint.company || "",
          location: location === "-" ? "" : location,
          city: city || ""
        });

        activeTravel = createResponse.data;
      }

      const updateResponse = await axios.put(API_BASE_URL + `/api/travel/${activeTravel._id}`, {
        status: nextAction.status,
        timeField: nextAction.timeField
      });

      const updatedTravel = updateResponse.data;
      setTravelByComplaintId((current) => ({
        ...current,
        [String(complaint._id)]: updatedTravel,
        [String(complaint.complaintId)]: updatedTravel
      }));
    } catch (error) {
      alert(error.response?.data?.msg || "Failed to update travel status");
    } finally {
      setUpdatingTravelId("");
    }
  };

  const filteredComplaints = useMemo(() => {
    return complaints.filter((complaint) =>
      [
        complaint.complaintId,
        complaint.company,
        complaint.productType,
        complaint.contactName,
        complaint.issue,
        complaint.status
      ]
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [complaints, search]);

  const getStatusColor = (status) => {
    switch (normalizeStatus(status)) {
      case "Completed":
        return "bg-green-100 text-green-700";
      case "Re-Visit":
        return "bg-rose-100 text-rose-700";
      case "Assigned":
        return "bg-blue-100 text-blue-700";
      case "In Progress":
        return "bg-purple-100 text-purple-700";
      default:
        return "bg-yellow-100 text-yellow-700";
    }
  };

  return (
    <EngineerLayout>
      <div className="mx-auto max-w-6xl space-y-4 sm:space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between md:gap-4">
          <div>
            <h1 className="text-[1.75rem] font-bold leading-tight text-gray-800 sm:text-2xl">My Complaints</h1>
            <p className="mt-1 text-sm text-gray-500">
              Complaints assigned to {engineerName || "the logged-in engineer"}
            </p>
          </div>

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search my complaints..."
            className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm md:w-80"
          />
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <SummaryCard label="Total" value={complaints.length} />
          <SummaryCard label="Pending" value={complaints.filter((item) => normalizeStatus(item.status) === "Pending").length} />
          <SummaryCard label="Completed" value={complaints.filter((item) => normalizeStatus(item.status) === "Completed").length} />
        </div>

        <div className="space-y-3 sm:space-y-4">
          {loading && (
            <div className="bg-white shadow rounded-2xl p-6 text-center text-gray-500 border border-gray-200">
              Loading complaints...
            </div>
          )}

          {!loading && filteredComplaints.length === 0 && (
            <div className="bg-white shadow rounded-2xl p-6 text-center text-gray-500 border border-gray-200">
              No complaints assigned
            </div>
          )}

          {!loading && filteredComplaints.map((complaint) => {
            const linkedReport =
              reportByComplaintId[complaint._id] ||
              reportByComplaintId[complaint.complaintId];
            const isAccepted = complaint.assignmentStatus === "accepted";
            const approvalStatus = normalizeApprovalStatus(linkedReport?.approvalStatus);
            const isApproved = approvalStatus === "Approved";
            const isChangesRequested = approvalStatus === "Changes Requested";
            const linkedTravel =
              travelByComplaintId[complaint._id] ||
              travelByComplaintId[complaint.complaintId];
            const nextTravelAction = getNextTravelAction(linkedTravel);
            const canCreateReport = canCreateReportFromTravel(linkedTravel);

            return (
              <div
                key={complaint._id}
                className="rounded-2xl border border-gray-200 bg-white p-4 shadow sm:p-6"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg font-bold text-gray-900">
                        {complaint.complaintId}
                      </h2>
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${getStatusColor(complaint.status)}`}
                      >
                        {normalizeStatus(complaint.status)}
                      </span>
                      {!isAccepted && (
                        <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-amber-700">
                          Awaiting Acceptance
                        </span>
                      )}
                    </div>

                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 sm:text-sm">
                      <span>Engineer: {complaint.engineerAssigned || "-"}</span>
                      <span>Visit: {complaint.visitDate || "-"}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                    {!isAccepted ? (
                      <button
                        type="button"
                        onClick={() => acceptComplaint(complaint._id)}
                        disabled={acceptingComplaintId === complaint._id}
                        className="rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300 sm:px-4"
                      >
                        {acceptingComplaintId === complaint._id ? "Accepting..." : "Accept Complaint"}
                      </button>
                    ) : linkedReport ? (
                      <>
                        <button
                          onClick={() => handleTravelAction(complaint)}
                          disabled={!nextTravelAction || updatingTravelId === complaint._id}
                          className="rounded-xl bg-amber-500 px-3 py-2 text-sm font-semibold text-white transition hover:bg-amber-600 sm:px-4"
                        >
                          {updatingTravelId === complaint._id
                            ? "Updating..."
                            : nextTravelAction?.label || "Reached Home"}
                        </button>

                        <button
                          onClick={() =>
                            !isApproved && navigate(`/create-report/${linkedReport._id}`)
                          }
                          disabled={isApproved}
                          className={`rounded-xl px-3 py-2 text-sm font-semibold text-white ${
                            isApproved
                              ? "bg-gray-400 cursor-not-allowed"
                              : isChangesRequested
                              ? "bg-rose-600 hover:bg-rose-700"
                              : "bg-blue-600 hover:bg-blue-700"
                          }`}
                        >
                          {isApproved ? "Report Approved" : isChangesRequested ? "Redo Report" : "Edit Report"}
                        </button>

                        <button
                          onClick={() => navigate(`/my-reports/${linkedReport._id}`)}
                          className="rounded-xl bg-gray-900 px-3 py-2 text-sm font-semibold text-white transition hover:bg-black sm:px-4"
                        >
                          View Report
                        </button>

                        <button
                          onClick={() => downloadReportPdf(linkedReport._id)}
                          className="rounded-xl bg-green-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-green-700 sm:px-4"
                        >
                          Download PDF
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleTravelAction(complaint)}
                          disabled={!nextTravelAction || updatingTravelId === complaint._id}
                          className="rounded-xl bg-amber-500 px-3 py-2 text-sm font-semibold text-white transition hover:bg-amber-600 sm:px-4"
                        >
                          {updatingTravelId === complaint._id
                            ? "Updating..."
                            : nextTravelAction?.label || "Reached Home"}
                        </button>
                        <button
                          onClick={() => canCreateReport && navigate(`/create-report/${complaint._id}`)}
                          disabled={!canCreateReport}
                          className={`rounded-xl px-3 py-2 text-sm font-semibold text-white ${
                            canCreateReport
                              ? "bg-blue-600 hover:bg-blue-700"
                              : "bg-gray-400 cursor-not-allowed"
                          }`}
                        >
                          Create Report
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 text-sm md:grid-cols-2 lg:grid-cols-4 lg:gap-4">
                  <Field label="Customer / Company" value={complaint.company || "-"} />
                  <Field label="Product Type" value={complaint.productType || "-"} />
                  <Field
                    label="Contact"
                    value={
                      <div className="space-y-1">
                        <div>{complaint.contactName || "-"}</div>
                        <PhoneActions phone={complaint.contactPhone || ""} />
                      </div>
                    }
                  />
                </div>

                {!linkedReport && isAccepted && !canCreateReport && (
                  <p className="mt-3 text-sm font-medium text-amber-700">
                    Create Report becomes available after travel status is updated to At Site.
                  </p>
                )}

                {complaint.address && (
                  <div className="mt-3 text-sm">
                    <Field label="Address" value={complaint.address} />
                  </div>
                )}

                <div className="mt-4">
                  <p className="text-gray-500 text-sm">Complaint Details</p>
                  <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-3.5 text-sm text-gray-800 sm:p-4">
                    {complaint.issue || "-"}
                  </div>
                </div>

                {complaint.siteAdvice && (
                  <div className="mt-4">
                    <p className="text-gray-500 text-sm">Site Advice</p>
                    <div className="mt-2 rounded-xl border border-sky-200 bg-sky-50 p-3.5 text-sm text-gray-800 sm:p-4">
                      {complaint.siteAdvice}
                    </div>
                  </div>
                )}

              </div>
            );
          })}
        </div>
      </div>
    </EngineerLayout>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-3 py-3 shadow sm:p-5">
      <p className="text-xs uppercase tracking-wide text-gray-500 sm:text-sm sm:normal-case sm:tracking-normal">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900 sm:mt-2 sm:text-3xl">{value}</p>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="min-w-0 rounded-xl border border-gray-100 bg-gray-50/70 px-3 py-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <div className="mt-1 break-words text-sm font-medium text-gray-800">{value}</div>
    </div>
  );
}

export default MyComplaints;


